import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, Info, Package, ClipboardList, AlertCircle } from 'lucide-react';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import { SERVICES } from '../data/content';

function DetailBlock({ icon: Icon, title, children, testid }) {
  return (
    <div>
      <h3 className="flex items-center gap-2.5 font-heading text-base font-bold tracking-tight text-charcoal">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-charcoal">
          <Icon className="h-4 w-4 text-lime" aria-hidden="true" />
        </span>
        {title}
      </h3>
      <div className="mt-3 text-sm leading-relaxed text-stonebrand" data-testid={testid}>
        {children}
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Services — Websites, Google Profiles & Social Setup for Tradespeople | Build My Trade"
        description="Website design, Google Business Profile setup, Facebook and Instagram pages, logo and branding, social content and ongoing website care — all for UK tradespeople."
        path="/services"
      />
      <PageHero
        eyebrow="Services"
        title="What we do, in plain English"
        intro="Six services that cover everything a trade business needs online. Each section tells you what it is, why it matters, what we do, what we need from you — and what nobody can honestly promise."
        crumbs={[{ label: 'Services' }]}
        testid="services-page-heading"
      />

      <div className="bg-offwhite">
        {SERVICES.map((s, idx) => (
          <section
            key={s.slug}
            id={s.slug}
            className={`scroll-mt-24 border-b border-hairline ${idx % 2 === 1 ? 'bg-white' : 'bg-offwhite'}`}
            data-testid={`service-section-${s.slug}`}
          >
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
              <div className="grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-4">
                  <Reveal>
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-charcoal">
                      <s.icon className="h-6 w-6 text-lime" aria-hidden="true" />
                    </span>
                    <p className="mt-6 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                      Service {String(idx + 1).padStart(2, '0')}
                    </p>
                    <h2 className="mt-3 font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl">
                      {s.title}
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-stonebrand">{s.summary}</p>
                    <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-lime px-4 py-2 text-xs font-semibold text-charcoal">
                      <Package className="h-3.5 w-3.5" aria-hidden="true" />
                      {s.includedIn}
                    </p>
                  </Reveal>
                </div>
                <div className="lg:col-span-8">
                  <div className="grid gap-8 rounded-2xl border border-hairline bg-white p-7 sm:grid-cols-2 sm:p-9">
                    <Reveal>
                      <DetailBlock icon={Info} title="What it is" testid={`service-what-${s.slug}`}>
                        <p>{s.what}</p>
                      </DetailBlock>
                    </Reveal>
                    <Reveal delay={0.05}>
                      <DetailBlock icon={Info} title="Why you need it" testid={`service-why-${s.slug}`}>
                        <p>{s.why}</p>
                      </DetailBlock>
                    </Reveal>
                    <Reveal delay={0.1}>
                      <DetailBlock icon={Check} title="What we do" testid={`service-wedo-${s.slug}`}>
                        <ul className="space-y-2">
                          {s.weDo.map((item, i) => (
                            <li key={i} className="flex gap-2.5">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-charcoal" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </DetailBlock>
                    </Reveal>
                    <Reveal delay={0.15}>
                      <DetailBlock icon={ClipboardList} title="What you provide" testid={`service-provide-${s.slug}`}>
                        <ul className="space-y-2">
                          {s.youProvide.map((item, i) => (
                            <li key={i} className="flex gap-2.5">
                              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-charcoal/40" aria-hidden="true" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </DetailBlock>
                    </Reveal>
                    <Reveal delay={0.2} className="sm:col-span-2">
                      <div className="rounded-xl bg-offwhite p-5">
                        <DetailBlock icon={AlertCircle} title="What’s not guaranteed" testid={`service-notguaranteed-${s.slug}`}>
                          <ul className="space-y-2">
                            {s.notGuaranteed.map((item, i) => (
                              <li key={i} className="flex gap-2.5">
                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-charcoal/40" aria-hidden="true" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </DetailBlock>
                      </div>
                    </Reveal>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="bg-charcoal-deep bg-blueprint" data-testid="services-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                One point of contact. <span className="text-lime">Everything handled.</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your trade business and we’ll recommend the right combination — never more than you
                need.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <ArrowLink to="/contact" variant="lime" testid="services-cta-quote" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
                <ArrowLink to="/pricing" variant="outline-light" testid="services-cta-pricing" className="px-8 py-4">
                  See Package Pricing
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
